import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import {GiftCardEntryData} from '../../../../models/schemas/giftCardsSchema';
import {useStores} from '../../../../stores/use-stores';
import {centsToDollarsString} from '../../../../utils/functions';
import {ZenGiftCardsPaymentInstrumentWithChargedAmount} from '../../../../utils/giftCards';
import {DollarSignIcon, TrashIconButton} from '../../../icons';
import Button from '../../../themed/Button';
import ActivityIndicator from '../../../ui-controls/ActivityIndicator';
import TextInput from '../../../ui-controls/form/TextInput';
import {ZenGiftCardEntryModal} from '../ZenGiftCardEntryModal';

type ZenGiftCardsListProps = {
  zenGiftCardPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[];
  onDeleteGiftCard: (id: string) => void;
};

const ZenGiftCardsList = ({
  zenGiftCardPaymentInstruments,
  onDeleteGiftCard,
}: ZenGiftCardsListProps) => (
  <>
    {!!zenGiftCardPaymentInstruments.length && (
      <View style={cardListStyles.main}>
        {zenGiftCardPaymentInstruments.map(zenGiftCardPaymentInstrument => (
          <ZenGiftCard
            zenGiftCardPaymentInstrument={zenGiftCardPaymentInstrument}
            key={zenGiftCardPaymentInstrument.id}
            onDelete={() => onDeleteGiftCard(zenGiftCardPaymentInstrument.id)}
          />
        ))}
      </View>
    )}
  </>
);

type ZenGiftCardProps = {
  zenGiftCardPaymentInstrument: ZenGiftCardsPaymentInstrumentWithChargedAmount;
  onDelete: () => void;
};

const ZenGiftCard = observer(
  ({zenGiftCardPaymentInstrument, onDelete}: ZenGiftCardProps) => {
    const {
      zen_card_number: number,
      chargedAmount = 0,
      balance = 0,
    } = zenGiftCardPaymentInstrument;

    const maskedNumber = `*${number.slice(-4)}`;
    const formattedBalance = centsToDollarsString(balance);
    const formattedAmount = centsToDollarsString(chargedAmount);

    return (
      <View
        style={{
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'space-around',
          marginBottom: 16,
        }}>
        <TextInput
          containerStyle={{width: 90}}
          disabled
          inputStyle={{width: 45}}
          label="Number"
          labelStyle={{fontSize: 14}}
          renderErrorMessage={false}
          value={maskedNumber}
        />
        <TextInput
          containerStyle={{width: 90}}
          disabled
          inputStyle={{width: 45}}
          label="Balance"
          labelStyle={{fontSize: 14}}
          leftIcon={<DollarSignIcon disabled />}
          renderErrorMessage={false}
          value={formattedBalance}
        />
        <TextInput
          containerStyle={{width: 100}}
          disabled
          inputStyle={{width: 50}}
          label="Amount"
          labelStyle={{fontSize: 14}}
          leftIcon={<DollarSignIcon disabled />}
          renderErrorMessage={false}
          value={formattedAmount}
        />
        <TrashIconButton onPress={onDelete} />
      </View>
    );
  },
);

const ZenGiftCardsManager = observer(() => {
  const {cartStore, paymentStore} = useStores();

  const [isGiftCardEntryModalVisible, setIsGiftCardEntryModalVisible] =
    useState(false);
  const [isAddCardSubmitting, setIsAddCardSubmitting] = useState(false);
  const [cardAddingError, setCardAddingError] = useState<string>('');

  if (paymentStore.isLoadingPaymentServices) {
    return <ActivityIndicator />;
  }

  if (!paymentStore.zenGiftCardsPaymentService) {
    console.debug('No Zen Gift Cards Payment Service available in this Store.');
    return null;
  }

  const zenGiftCardsWithAmount = cartStore.zenGiftCardsUserPaymentInstruments;

  const handleGiftCardEntryModalCancel = () => {
    setIsGiftCardEntryModalVisible(false);
    setCardAddingError('');
  };

  const handleAddCardPress = () => setIsGiftCardEntryModalVisible(true);
  const handleAddCard = async (formValues: GiftCardEntryData) => {
    try {
      setIsAddCardSubmitting(true);
      await cartStore.addZenGiftCardsUserPaymentInstrument(
        formValues.number,
        formValues.pin,
      );
      setIsGiftCardEntryModalVisible(false);
      setCardAddingError('');
    } catch (error: any) {
      setCardAddingError(error.message);
    }
    setIsAddCardSubmitting(false);
  };

  const handleDeleteGiftCard = (id: string) =>
    cartStore.deleteZenGiftCardsUserPaymentInstrument(id);

  return (
    <View>
      <Button
        title="USE A GIFT CARD"
        variant="text"
        onPress={handleAddCardPress}
        icon={{name: 'add'}}
        iconRight
        containerStyle={{marginVertical: 0}}
        buttonStyle={{justifyContent: 'space-between'}}
        titleStyle={{fontSize: 16, fontWeight: '600'}}
      />
      <ZenGiftCardsList
        zenGiftCardPaymentInstruments={zenGiftCardsWithAmount}
        onDeleteGiftCard={handleDeleteGiftCard}
      />
      <ZenGiftCardEntryModal
        isSubmitting={isAddCardSubmitting}
        isVisible={isGiftCardEntryModalVisible}
        onAdd={handleAddCard}
        onCancel={handleGiftCardEntryModalCancel}
        error={cardAddingError}
      />
    </View>
  );
});

export default ZenGiftCardsManager;

const cardListStyles = StyleSheet.create({
  main: {
    padding: 8,
    borderColor: 'grey',
    borderWidth: 0.4,
    borderRadius: 4,
  },
});
